import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Stack,
  Typography,
} from "@mui/joy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RefreshIcon from "@mui/icons-material/Refresh";
import { QCSectionResult, QCSectionStatus } from "../../types/qcTypes";
import { getFriendlyQCErrorMessage } from "../../utils/qcErrorMessages";
import {
  partialPartsLabel,
  partialSectionMessage,
} from "../../utils/qcPartialSections";

interface QCProgressViewProps {
  sections: QCSectionResult[];
  totalSections: number;
  completedSections: number;
  totalFindings: number;
  status: "running" | "completed" | "failed";
  errorMessage?: string;
  // Task #491: SSE-stream reconnect feedback.
  reconnectAttempt?: number;
  reconnectMax?: number;
  onRetry?: () => void;
}

const statusIcon: Record<QCSectionStatus, React.ReactNode> = {
  pending: <RadioButtonUncheckedIcon sx={{ color: "neutral.400" }} />,
  checking: <CircularProgress size="sm" />,
  complete: <CheckCircleIcon sx={{ color: "success.500" }} />,
  error: <ErrorIcon sx={{ color: "danger.500" }} />,
};

const QCProgressView: React.FC<QCProgressViewProps> = ({
  sections,
  totalSections,
  completedSections,
  totalFindings,
  status,
  errorMessage,
  reconnectAttempt,
  reconnectMax,
  onRetry,
}) => {
  const progress =
    totalSections > 0 ? (completedSections / totalSections) * 100 : 0;
  // Until the run reports its section count there is nothing to count.
  const preparing = status === "running" && totalSections === 0;
  const isReconnecting =
    status === "running" &&
    reconnectAttempt !== undefined &&
    reconnectAttempt > 0;

  return (
    <Box>
      {/* Overall progress */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography level="body-sm">
            {preparing
              ? "Preparing…"
              : `Checking sections… ${completedSections}/${totalSections}`}
          </Typography>
          {!preparing && (
            <Typography level="body-sm">
              {totalFindings} finding{totalFindings !== 1 ? "s" : ""} so far
            </Typography>
          )}
        </Stack>
        <LinearProgress
          determinate={!preparing}
          value={preparing ? undefined : progress}
          aria-label="Quality check progress"
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      {/* Reconnecting banner */}
      {isReconnecting && (
        <Alert
          color="warning"
          variant="soft"
          size="sm"
          startDecorator={<CircularProgress size="sm" color="warning" />}
          sx={{ mb: 2 }}
        >
          Connection lost. Reconnecting…
          {reconnectMax ? ` (${reconnectAttempt}/${reconnectMax})` : ""}
        </Alert>
      )}

      {/* Section list */}
      <List size="sm">
        {sections.map((section) => (
          <ListItem key={section.sectionIndex}>
            <ListItemDecorator>{statusIcon[section.status]}</ListItemDecorator>
            <ListItemContent>
              <Typography level="body-sm">{section.sectionTitle}</Typography>
              {section.status === "error" && section.partial && (
                <Typography level="body-xs" color="warning">
                  {partialSectionMessage(section.partial)}
                </Typography>
              )}
              {section.status === "error" &&
                !section.partial &&
                section.errorMessage && (
                  <Typography level="body-xs" color="danger">
                    {getFriendlyQCErrorMessage(section.errorMessage)}
                  </Typography>
                )}
            </ListItemContent>
            {section.status === "error" && section.partial && (
              <Typography level="body-xs" color="warning">
                {partialPartsLabel(section.partial)}
              </Typography>
            )}
            {section.status === "error" && !section.partial && (
              <Typography level="body-xs" color="danger">
                Failed
              </Typography>
            )}
            {section.status === "complete" && section.findingCount > 0 && (
              <Typography level="body-xs" color="warning">
                {section.findingCount} finding
                {section.findingCount !== 1 ? "s" : ""}
              </Typography>
            )}
            {section.status === "complete" && section.findingCount === 0 && (
              <Typography level="body-xs" color="success">
                No issues
              </Typography>
            )}
          </ListItem>
        ))}
      </List>

      {/* Error display + retry */}
      {status === "failed" && errorMessage && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: "sm",
            bgcolor: "danger.softBg",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Typography level="body-sm" color="danger">
              {getFriendlyQCErrorMessage(errorMessage)}
            </Typography>
            {onRetry && (
              <Button
                size="sm"
                variant="outlined"
                color="danger"
                startDecorator={<RefreshIcon />}
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default QCProgressView;
